import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { normalizePhoneNumber } from "../services/spinnerOtpService";
import {
    grantUserOfferForService,
    getOffersForPhone,
} from "../services/userOffersService";

/**
 * UserOffers (admin)
 * ------------------
 * Grant a discount to a specific user for a specific service. Writes to
 * UserOffers/{phone}/services/{serviceSlug} — the same doc the spinner and the
 * booking-time offer logic read. `serviceSlug` is a Category id; the Cloud
 * Function marks it `used: true` after redemption, so granting resets it.
 */

const DISCOUNT_TYPES = {
    percentage: "percentage",
    flat: "flat",
};

const EMPTY_FORM = {
    phoneNumber: "",
    serviceSlug: "",
    discountType: DISCOUNT_TYPES.percentage,
    value: "",
};

const UserOffers = () => {
    const [categories, setCategories] = useState([]);
    const [form, setForm] = useState(EMPTY_FORM);
    const [existingOffers, setExistingOffers] = useState([]);
    const [loadingOffers, setLoadingOffers] = useState(false);

    const [validationErrors, setValidationErrors] = useState({});
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const snap = await getDocs(collection(db, "Categories"));
                setCategories(
                    snap.docs
                        .map((d) => ({ id: d.data().id ?? d.id, name: d.data().name }))
                        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                );
            } catch (err) {
                console.error("Error loading categories:", err);
                setError("Failed to load services.");
            }
        };
        fetchCategories();
    }, []);

    const phoneDigits = normalizePhoneNumber(form.phoneNumber);
    const phoneReady = phoneDigits.length === 10;

    const categoryNameById = useMemo(() => {
        const map = {};
        categories.forEach((c) => (map[c.id] = c.name));
        return map;
    }, [categories]);

    // Load the user's existing offers whenever a full phone number is entered.
    useEffect(() => {
        if (!phoneReady) {
            setExistingOffers([]);
            return;
        }
        let cancelled = false;
        setLoadingOffers(true);
        getOffersForPhone(phoneDigits)
            .then((offers) => {
                if (!cancelled) setExistingOffers(offers);
            })
            .catch((err) => console.error("Error loading user offers:", err))
            .finally(() => {
                if (!cancelled) setLoadingOffers(false);
            });
        return () => {
            cancelled = true;
        };
    }, [phoneDigits, phoneReady]);

    const getFieldClass = (field, extra = "") =>
        [
            "rounded border p-2 text-gray-900 focus:outline-none focus:ring-1",
            validationErrors[field]
                ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
            extra,
        ]
            .filter(Boolean)
            .join(" ");

    const getLabelClass = (field) =>
        `grid gap-1 text-sm font-medium ${validationErrors[field] ? "text-red-700" : "text-gray-700"
        }`;

    const renderFieldError = (field) =>
        validationErrors[field] ? (
            <span className="text-xs font-medium text-red-600">
                {validationErrors[field]}
            </span>
        ) : null;

    const clearFieldError = (field) =>
        setValidationErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (value?.toString().trim()) clearFieldError(field);
        setSuccess("");
    };

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setValidationErrors({});
        setError("");
    };

    const refreshOffers = async () => {
        if (!phoneReady) return;
        setLoadingOffers(true);
        try {
            setExistingOffers(await getOffersForPhone(phoneDigits));
        } finally {
            setLoadingOffers(false);
        }
    };

    const validate = () => {
        const errors = {};
        if (!phoneReady)
            errors.phoneNumber = "Enter a valid 10-digit phone number.";
        if (!form.serviceSlug) errors.serviceSlug = "Select a service.";

        const numeric = Number(form.value);
        if (form.value === "" || isNaN(numeric) || numeric <= 0) {
            errors.value = "Enter a discount greater than 0.";
        } else if (
            form.discountType === DISCOUNT_TYPES.percentage &&
            numeric > 100
        ) {
            errors.value = "Percentage cannot exceed 100.";
        }
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccess("");

        const errors = validate();
        if (Object.keys(errors).length) {
            setValidationErrors(errors);
            setError("Please fix the highlighted fields.");
            return;
        }

        const numeric = Number(form.value);
        const alreadyHasOffer = existingOffers.some(
            (o) => o.serviceSlug === form.serviceSlug
        );
        if (
            alreadyHasOffer &&
            !window.confirm(
                "This user already has an offer for this service. Overwrite it?"
            )
        ) {
            return;
        }

        setSaving(true);
        setError("");
        setValidationErrors({});
        try {
            await grantUserOfferForService({
                phoneNumber: phoneDigits,
                serviceSlug: form.serviceSlug,
                offer:
                    form.discountType === DISCOUNT_TYPES.percentage
                        ? { percentageOff: numeric, flatOff: null }
                        : { percentageOff: null, flatOff: numeric },
            });

            const serviceName =
                categoryNameById[form.serviceSlug] || form.serviceSlug;
            setSuccess(
                `Granted ${form.discountType === DISCOUNT_TYPES.percentage
                    ? `${numeric}% off`
                    : `₹${numeric} off`
                } on ${serviceName} to ${phoneDigits}.`
            );
            setForm((prev) => ({ ...EMPTY_FORM, phoneNumber: prev.phoneNumber }));
            await refreshOffers();
        } catch (err) {
            console.error("Error granting offer:", err);
            setError(err.message || "Failed to grant offer.");
        } finally {
            setSaving(false);
        }
    };

    const formatDiscount = (o) => {
        if (o.percentageOff != null) return `${o.percentageOff}% OFF`;
        if (o.flatOff != null) return `₹${o.flatOff} OFF`;
        return "—";
    };

    return (
        <div className="h-screen overflow-y-auto p-4 bg-gray-50">
            <div className="max-w-5xl mx-auto w-full">
                <h1 className="text-3xl font-bold mb-6">User Offers</h1>

                <form
                    onSubmit={handleSubmit}
                    className="bg-white border border-gray-200 rounded-md shadow-sm mb-8 overflow-hidden"
                >
                    <div className="border-b border-gray-200 p-4">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Grant an Offer
                        </h2>
                        <p className="text-sm text-gray-500">
                            Give a specific customer a discount on a specific service.
                        </p>
                    </div>

                    {(error || success) && (
                        <div className="px-4 pt-4">
                            {error && <p className="text-sm text-red-600">{error}</p>}
                            {success && (
                                <p className="text-sm font-medium text-green-600">
                                    {success}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="space-y-6 p-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <label className={getLabelClass("phoneNumber")}>
                                Customer phone number
                                <div
                                    className={getFieldClass(
                                        "phoneNumber",
                                        "flex items-center gap-2 p-0 overflow-hidden"
                                    )}
                                >
                                    <span className="select-none border-r border-gray-300 bg-gray-50 px-3 py-2 text-gray-600">
                                        +91
                                    </span>
                                    <input
                                        type="tel"
                                        inputMode="numeric"
                                        maxLength={10}
                                        placeholder="10-digit number"
                                        className="min-w-0 flex-1 bg-transparent py-2 pr-2 text-gray-900 focus:outline-none"
                                        value={form.phoneNumber}
                                        onChange={(e) =>
                                            setField(
                                                "phoneNumber",
                                                e.target.value.replace(/\D/g, "").slice(-10)
                                            )
                                        }
                                    />
                                </div>
                                {renderFieldError("phoneNumber")}
                            </label>
                            <label className={getLabelClass("serviceSlug")}>
                                Service
                                <select
                                    className={getFieldClass("serviceSlug")}
                                    value={form.serviceSlug}
                                    onChange={(e) =>
                                        setField("serviceSlug", e.target.value)
                                    }
                                >
                                    <option value="">Select service</option>
                                    {categories.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                                {renderFieldError("serviceSlug")}
                            </label>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <label className="grid gap-1 text-sm font-medium text-gray-700">
                                Discount type
                                <select
                                    className="rounded border border-gray-300 p-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    value={form.discountType}
                                    onChange={(e) =>
                                        setField("discountType", e.target.value)
                                    }
                                >
                                    <option value={DISCOUNT_TYPES.percentage}>
                                        Percentage (%)
                                    </option>
                                    <option value={DISCOUNT_TYPES.flat}>
                                        Flat amount (₹)
                                    </option>
                                </select>
                            </label>
                            <label className={getLabelClass("value")}>
                                {form.discountType === DISCOUNT_TYPES.percentage
                                    ? "Percentage off"
                                    : "Flat amount off (₹)"}
                                <input
                                    type="number"
                                    min="1"
                                    max={
                                        form.discountType === DISCOUNT_TYPES.percentage
                                            ? "100"
                                            : undefined
                                    }
                                    step="1"
                                    placeholder={
                                        form.discountType === DISCOUNT_TYPES.percentage
                                            ? "e.g. 20"
                                            : "e.g. 150"
                                    }
                                    className={getFieldClass("value")}
                                    value={form.value}
                                    onChange={(e) => setField("value", e.target.value)}
                                />
                                {renderFieldError("value")}
                            </label>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-end">
                        <button
                            type="button"
                            onClick={resetForm}
                            className="rounded border border-gray-300 px-5 py-2 font-medium text-gray-700 hover:bg-white"
                        >
                            Clear
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="rounded bg-green-600 px-6 py-2 font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
                        >
                            {saving ? "Granting…" : "Grant Offer"}
                        </button>
                    </div>
                </form>

                {/* Existing offers for the entered phone number */}
                <div className="bg-white border border-gray-200 rounded-md shadow-sm">
                    <div className="flex items-center justify-between border-b border-gray-200 p-4">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Offers for this customer
                        </h2>
                        {loadingOffers && (
                            <span className="text-sm text-gray-400">Loading…</span>
                        )}
                    </div>
                    {!phoneReady ? (
                        <p className="p-4 text-sm text-gray-500">
                            Enter a 10-digit phone number to see their existing offers.
                        </p>
                    ) : existingOffers.length === 0 ? (
                        <p className="p-4 text-sm text-gray-500">
                            No offers found for {phoneDigits}.
                        </p>
                    ) : (
                        <table className="min-w-full border-collapse">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="border p-2 text-left">Service</th>
                                    <th className="border p-2 text-left">Discount</th>
                                    <th className="border p-2 text-left">Status</th>
                                    <th className="border p-2 text-left">Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {existingOffers.map((o) => (
                                    <tr key={o.serviceSlug}>
                                        <td className="border p-2">
                                            {categoryNameById[o.serviceSlug] || o.serviceSlug}
                                        </td>
                                        <td className="border p-2 font-medium">
                                            {formatDiscount(o)}
                                        </td>
                                        <td className="border p-2">
                                            {o.used ? (
                                                <span className="text-gray-500">Redeemed</span>
                                            ) : (
                                                <span className="text-green-600">Available</span>
                                            )}
                                        </td>
                                        <td className="border p-2 text-sm text-gray-500">
                                            {o.updatedAt
                                                ? new Date(o.updatedAt).toLocaleString()
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserOffers;
